import type { MailboxInfo, MailboxKey } from "owa-client-types";
import { getMailboxKeyForMailboxInfo } from "owa-client-types";

export interface MailboxKeyGroup<T> {
    mailboxInfo: MailboxInfo;
    items: T[];
}

/**
 * Groups items by the MailboxKey of the MailboxInfo returned by getItemMailboxInfo,
 * bundling each group's resolved MailboxInfo alongside its items. Insertion order of
 * first occurrence per mailbox is preserved.
 *
 * The resolver callback lets callers choose the mailbox source (e.g. a value already
 * carried on the item vs. a store lookup) and may throw if resolution is required but
 * fails.
 */
export function groupByMailboxKey<T>(items: T[], getItemMailboxInfo: (item: T) => MailboxInfo): Map<MailboxKey, MailboxKeyGroup<T>> {
    const groups = new Map<MailboxKey, MailboxKeyGroup<T>>();
    
    for (const item of items) {
        const mailboxInfo = getItemMailboxInfo(item);
        const key = getMailboxKeyForMailboxInfo(mailboxInfo);
        const existing = groups.get(key);
        if (existing) {
            existing.items.push(item);
        }
        else {
            groups.set(key, { mailboxInfo, items: [item] });
        }
    }
    
    return groups;
}
